import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { AlertTriangle, Trash2, Pencil, Save, X, Eye, ShieldAlert } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// America/Hermosillo (UTC-7, no DST). All time clock UI uses this fixed offset
// regardless of the browser timezone so the admin always sees Sonora local time.
const TZ_OFFSET_HOURS = -7;
const TZ_OFFSET_MS = TZ_OFFSET_HOURS * 60 * 60 * 1000;
const pad2 = (n) => String(n).padStart(2, '0');

// UTC ISO string → "YYYY-MM-DDTHH:MM" in Hermosillo local time (for datetime-local input).
function utcIsoToHermosilloInput(iso) {
  if (!iso) return '';
  const d = new Date(iso); // parses ISO with Z/offset correctly regardless of browser TZ
  if (isNaN(d.getTime())) return '';
  const shifted = new Date(d.getTime() + TZ_OFFSET_MS);
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}T${pad2(shifted.getUTCHours())}:${pad2(shifted.getUTCMinutes())}`;
}

// "YYYY-MM-DDTHH:MM" (Hermosillo local) → UTC ISO string.
function hermosilloInputToUtcIso(s) {
  if (!s) return null;
  const [date, time] = s.split('T');
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const ms = Date.UTC(y, m - 1, d, hh, mm, 0) - TZ_OFFSET_MS;
  return new Date(ms).toISOString();
}

// Format a UTC ISO string as "DD/MM/YYYY HH:MM" in Hermosillo time for display.
const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const s = new Date(d.getTime() + TZ_OFFSET_MS);
  return `${pad2(s.getUTCDate())}/${pad2(s.getUTCMonth() + 1)}/${s.getUTCFullYear()} ${pad2(s.getUTCHours())}:${pad2(s.getUTCMinutes())}`;
};
const STATUS_LABEL = {
  open: { label: 'Abierto', cls: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completado', cls: 'bg-emerald-100 text-emerald-700' },
  auto_closed: { label: 'Auto-cerrado', cls: 'bg-amber-100 text-amber-700' },
  absence: { label: 'Falta', cls: 'bg-red-100 text-red-700' },
  vacation: { label: 'Vacaciones', cls: 'bg-purple-100 text-purple-700' },
};

export default function Records() {
  const { getAuthHeaders } = useAuth();
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterEmp, setFilterEmp] = useState('');
  // Default filter range = current week in America/Hermosillo (Monday → Sunday).
  // Any other range must be picked by the user from the date inputs.
  const currentWeekRange = (() => {
    // Build "today" in Hermosillo (UTC-7) using a fixed offset.
    const HERMO_OFFSET_MS = -7 * 60 * 60 * 1000;
    const nowHermo = new Date(Date.now() + HERMO_OFFSET_MS);
    // getUTCDay() on the shifted date gives us the local day-of-week.
    const dow = nowHermo.getUTCDay(); // 0 Sun … 6 Sat
    const daysSinceMonday = (dow + 6) % 7;
    const fmt = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    const monday = new Date(nowHermo);
    monday.setUTCDate(nowHermo.getUTCDate() - daysSinceMonday);
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    return { from: fmt(monday), to: fmt(sunday) };
  })();
  const [filterFrom, setFilterFrom] = useState(currentWeekRange.from);
  const [filterTo, setFilterTo] = useState(currentWeekRange.to);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [auditOpen, setAuditOpen] = useState(null);
  const [waiveTarget, setWaiveTarget] = useState(null);
  const [waiveReason, setWaiveReason] = useState('');

  const load = async () => {
    const params = new URLSearchParams();
    if (filterEmp) params.append('employee_id', filterEmp);
    if (filterFrom) params.append('from', filterFrom);
    if (filterTo) params.append('to', filterTo);
    try {
      const r = await axios.get(`${API}/api/timeclock/records?${params}`, { headers: getAuthHeaders() });
      setList(r.data);
    } catch { /* noop */ }
  };
  const loadEmployees = async () => {
    try {
      const r = await axios.get(`${API}/api/timeclock/employees`, { headers: getAuthHeaders() });
      setEmployees(r.data);
    } catch { /* noop */ }
  };
  useEffect(() => { loadEmployees(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterEmp, filterFrom, filterTo]);

  const startEdit = (rec) => {
    setEditId(rec.id);
    setEditForm({
      clock_in: utcIsoToHermosilloInput(rec.clock_in),
      clock_out: utcIsoToHermosilloInput(rec.clock_out),
      status: rec.status,
      reason: '',
    });
  };

  const saveEdit = async () => {
    if (!editForm.reason || !editForm.reason.trim()) {
      alert('Motivo de edicion requerido');
      return;
    }
    const payload = { reason: editForm.reason };
    // Convert Hermosillo local input value back to UTC ISO before saving.
    // Independent of browser timezone — always uses fixed UTC-7 offset.
    if (editForm.clock_in) payload.clock_in = hermosilloInputToUtcIso(editForm.clock_in);
    if (editForm.clock_out) payload.clock_out = hermosilloInputToUtcIso(editForm.clock_out);
    if (editForm.status) payload.status = editForm.status;
    try {
      await axios.put(`${API}/api/timeclock/records/${editId}`, payload, { headers: getAuthHeaders() });
      setEditId(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const remove = async (rec) => {
    const reason = window.prompt('Motivo para eliminar el registro:');
    if (!reason || !reason.trim()) return;
    try {
      await axios.delete(`${API}/api/timeclock/records/${rec.id}`, {
        headers: getAuthHeaders(), data: { reason }
      });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const submitWaive = async () => {
    if (!waiveReason.trim()) return;
    try {
      await axios.post(`${API}/api/timeclock/records/${waiveTarget.id}/waive-penalty`,
        { reason: waiveReason }, { headers: getAuthHeaders() });
      setWaiveTarget(null);
      setWaiveReason('');
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-3" data-testid="tc-records-section">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end bg-white p-3 rounded-lg border border-slate-200">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Empleado</label>
          <select className="h-9 px-2 border border-slate-200 rounded-md text-sm min-w-[160px]"
            value={filterEmp} onChange={(e) => setFilterEmp(e.target.value)} data-testid="tc-filter-emp">
            <option value="">Todos</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Desde</label>
          <Input type="date" className="h-9 w-40 text-sm" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Hasta</label>
          <Input type="date" className="h-9 w-40 text-sm" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
        </div>
        {(filterEmp || filterFrom !== currentWeekRange.from || filterTo !== currentWeekRange.to) && (
          <Button variant="outline" className="h-9" onClick={() => { setFilterEmp(''); setFilterFrom(currentWeekRange.from); setFilterTo(currentWeekRange.to); }}>Limpiar</Button>
        )}
      </div>

      {/* Records Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2.5">Fecha</th>
              <th className="text-left px-3 py-2.5">Colaborador</th>
              <th className="text-left px-3 py-2.5">Entrada</th>
              <th className="text-left px-3 py-2.5">Salida</th>
              <th className="text-right px-3 py-2.5">Horas Checadas</th>
              <th className="text-right px-3 py-2.5">Horas Trabajadas</th>
              <th className="text-right px-3 py-2.5">Retardo</th>
              <th className="text-center px-3 py-2.5">Estatus</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-slate-400 text-xs">Sin registros</td></tr>
            ) : list.map(r => (
              <RecordRow key={r.id} rec={r}
                isEditing={editId === r.id} editForm={editForm} setEditForm={setEditForm}
                onStartEdit={() => startEdit(r)} onCancelEdit={() => setEditId(null)} onSaveEdit={saveEdit}
                onDelete={() => remove(r)} onAudit={() => setAuditOpen(r)}
                onWaive={() => { setWaiveTarget(r); setWaiveReason(''); }}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Audit panel */}
      {auditOpen && (
        <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="tc-audit-panel">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold">Auditoria — {auditOpen.employee_name} {auditOpen.date}</h3>
            <button onClick={() => setAuditOpen(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          {(auditOpen.audit_trail || []).length === 0 ? (
            <p className="text-xs text-slate-400">Sin entradas de auditoria</p>
          ) : (
            <div className="space-y-2">
              {auditOpen.audit_trail.map(a => (
                <div key={a.id} className="text-xs bg-slate-50 p-3 rounded border border-slate-100">
                  <div className="flex justify-between mb-1">
                    <span className="font-semibold capitalize">{a.action.replace('_', ' ')} — {a.admin}</span>
                    <span className="text-slate-400">{new Date(a.timestamp).toLocaleString('es-MX')}</span>
                  </div>
                  {a.reason && <p className="text-slate-600 italic mb-1">"{a.reason}"</p>}
                  {a.before && <pre className="text-[10px] text-slate-500 whitespace-pre-wrap">Antes: {JSON.stringify(a.before)}</pre>}
                  {a.after && <pre className="text-[10px] text-slate-500 whitespace-pre-wrap">Despues: {JSON.stringify(a.after)}</pre>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Waive panel */}
      {waiveTarget && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md" data-testid="tc-waive-panel">
          <h3 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" /> Ondonar penalizacion — {waiveTarget.employee_name}
          </h3>
          <Input placeholder="Motivo (requerido)" value={waiveReason}
            onChange={(e) => setWaiveReason(e.target.value)} data-testid="tc-waive-reason" />
          <div className="flex gap-2 mt-2">
            <Button onClick={submitWaive} disabled={!waiveReason.trim()} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tc-waive-confirm">Confirmar</Button>
            <Button variant="outline" onClick={() => setWaiveTarget(null)}>Cancelar</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RecordRow({ rec, isEditing, editForm, setEditForm, onStartEdit, onCancelEdit, onSaveEdit, onDelete, onAudit, onWaive }) {
  const st = STATUS_LABEL[rec.status] || { label: rec.status, cls: 'bg-slate-100 text-slate-600' };
  if (isEditing) {
    return (
      <tr className="border-t border-slate-100 bg-amber-50">
        <td className="px-3 py-2">{rec.date}</td>
        <td className="px-3 py-2 font-medium">{rec.employee_name}</td>
        <td className="px-3 py-2"><Input type="datetime-local" className="h-8 text-xs" value={editForm.clock_in || ''} onChange={(e) => setEditForm({ ...editForm, clock_in: e.target.value })} data-testid="tc-edit-ci" /></td>
        <td className="px-3 py-2"><Input type="datetime-local" className="h-8 text-xs" value={editForm.clock_out || ''} onChange={(e) => setEditForm({ ...editForm, clock_out: e.target.value })} data-testid="tc-edit-co" /></td>
        <td colSpan={4} className="px-3 py-2">
          <Input className="h-8 text-xs" placeholder="Motivo de edicion *" value={editForm.reason || ''} onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })} data-testid="tc-edit-reason" />
        </td>
        <td className="px-3 py-2">
          <select className="h-8 text-xs border border-slate-200 rounded px-1" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap">
          <button onClick={onSaveEdit} className="text-emerald-600 hover:text-emerald-700 mr-2" data-testid="tc-edit-save"><Save className="w-3.5 h-3.5 inline" /></button>
          <button onClick={onCancelEdit} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5 inline" /></button>
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-3 py-2 whitespace-nowrap">{rec.date}</td>
      <td className="px-3 py-2 font-medium">{rec.employee_name}</td>
      <td className="px-3 py-2 font-mono text-xs">{fmtTime(rec.clock_in)}</td>
      <td className="px-3 py-2 font-mono text-xs">{fmtTime(rec.clock_out)}</td>
      <td className="px-3 py-2 text-right">{(rec.worked_hours || 0).toFixed(2)}</td>
      <td className="px-3 py-2 text-right font-medium">{(rec.regular_hours || 0).toFixed(2)}</td>
      <td className="px-3 py-2 text-right">{rec.late_minutes ? `${rec.late_minutes}m` : '—'}</td>
      <td className="px-3 py-2 text-center">
        <span className={`status-badge ${st.cls}`}>{st.label}</span>
        {rec.auto_closed && !rec.penalty_waived && (
          <span title="Auto-cerrado con penalizacion" className="ml-1 inline-block">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 inline" />
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <button onClick={onStartEdit} className="text-[#1D3C6E] hover:underline mr-2 text-xs" data-testid={`tc-edit-rec-${rec.id}`}><Pencil className="w-3 h-3 inline" /></button>
        <button onClick={onAudit} className="text-slate-500 hover:text-slate-700 mr-2 text-xs" title="Auditoria"><Eye className="w-3 h-3 inline" /></button>
        {rec.auto_closed && !rec.penalty_waived && (
          <button onClick={onWaive} className="text-amber-600 hover:text-amber-700 mr-2 text-xs" title="Ondonar penalizacion" data-testid={`tc-waive-${rec.id}`}><ShieldAlert className="w-3 h-3 inline" /></button>
        )}
        <button onClick={onDelete} className="text-red-500 hover:text-red-700 text-xs"><Trash2 className="w-3 h-3 inline" /></button>
      </td>
    </tr>
  );
}
